// src/components/Video/Pagination.js
import React from 'react';
import PropTypes from 'prop-types';

// Helper function to generate page numbers with ellipsis
const getPageNumbers = (currentPage, totalPages, siblingCount = 1) => {
  const totalPageNumbers = siblingCount * 2 + 5;

  if (totalPages <= totalPageNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  const firstPageIndex = 1;
  const lastPageIndex = totalPages;
  const pages = [];

  pages.push(firstPageIndex);

  if (shouldShowLeftEllipsis) {
    pages.push('left-ellipsis');
  }

  for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
    if (i === firstPageIndex || i === lastPageIndex) continue;
    pages.push(i);
  }

  if (shouldShowRightEllipsis) {
    pages.push('right-ellipsis');
  }

  pages.push(lastPageIndex);

  return pages;
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  if (totalPages === 0) return null;

  return (
    <nav
      className="flex justify-center items-center flex-wrap gap-1 my-8"
      aria-label="Pagination Navigation"
    >
      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        aria-label="Previous Page"
        className="px-4 py-2 min-w-[80px] text-sm rounded-md bg-brand-red text-white border-none cursor-pointer transition-colors duration-300 hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
      >
        Previous
      </button>

      {pageNumbers.map((page, index) => {
        if (page === 'left-ellipsis' || page === 'right-ellipsis') {
          return (
            <span key={index} className="px-3 py-2 text-sm text-gray-500">
              ...
            </span>
          );
        }

        return (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            aria-current={page === currentPage ? 'page' : undefined}
            aria-label={`Go to page ${page}`}
            className={`min-w-[40px] px-3 py-2 text-sm rounded-md border cursor-pointer transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 ${
              page === currentPage
                ? 'bg-red-800 text-white border-red-800'
                : 'bg-white text-brand-red border-brand-red hover:bg-brand-orange hover:text-white hover:border-brand-orange'
            }`}
          >
            {page}
          </button>
        );
      })}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next Page"
        className="px-4 py-2 min-w-[80px] text-sm rounded-md bg-brand-red text-white border-none cursor-pointer transition-colors duration-300 hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
      >
        Next
      </button>
    </nav>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default React.memo(Pagination);
